import React, { useState, useEffect } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  Stepper, Step, StepLabel, StepContent,
  Button, Typography, TextField, Chip,
  Dialog, DialogTitle, DialogContent, DialogActions,
} from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/material.css";
import { API_BASE_URL } from "../config";

const FIELD_MAP = {
  "Name": "name",
  "Mobile Number": "mobile",
  "House Name": "house",
  "Place": "place",
  "Post Office": "post",
  "Pincode": "pincode",
  "District": "district",
  "State": "state",
};

const FIELD_LABELS = Object.keys(FIELD_MAP);

const PARAM_MAPPING = {
  phone: "mobile",
  name: "name",
  house: "house",
  place: "place",
  village: "place",
  post: "post",
  pin_code: "pincode",
  pincode: "pincode",
  district: "district",
  state: "state",
};

const PRESET_AMOUNTS = [100, 500, 1000, 2000, 5000];

const EMPTY_FORM = {
  name: "",
  mobile: "",
  house: "",
  place: "",
  post: "",
  pincode: "",
  district: "",
  state: "",
};

const steps = ["Enter Amount", "Personal Details"];

const Home = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Set on /r/:code - a referral link shared from the main app or the organiser app.
  const { code: referralCode } = useParams();
  const [referral, setReferral] = useState(null);
  const [referralError, setReferralError] = useState("");
  const [loading, setLoading] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [stepOneInput, setStepOneInput] = useState("");
  const [errorStepOne, setErrorStepOne] = useState(false);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [errorStepTwo, setErrorStepTwo] = useState(false);
  const [additionalParams, setAdditionalParams] = useState({});
  const [institutionName, setInstitutionName] = useState(null);
  const [mobileDialCode, setMobileDialCode] = useState("91");
  const [otpDialogOpen, setOtpDialogOpen] = useState(false);
  const [otpValue, setOtpValue] = useState("");
  const [otpSending, setOtpSending] = useState(false);
  const [otpVerifying, setOtpVerifying] = useState(false);
  const [otpError, setOtpError] = useState("");

  useEffect(() => {
    const updatedFields = {};
    const extraParams = {};
    let amountFromUrl = "";

    for (const [key, value] of searchParams.entries()) {
      const lowerKey = key.toLowerCase();
      if (lowerKey === "amount") {
        amountFromUrl = value;
      } else if (PARAM_MAPPING[lowerKey]) {
        const fieldName = PARAM_MAPPING[lowerKey];
        updatedFields[fieldName] =
          fieldName === "mobile"
            ? decodeURIComponent(value).replace(/^\+/, "")
            : decodeURIComponent(value.replace(/\+/g, " "));
      } else {
        extraParams[key] = decodeURIComponent(value.replace(/\+/g, " "));
      }
    }

    if (amountFromUrl) setStepOneInput(amountFromUrl);
    setFormData((prev) => ({ ...prev, ...updatedFields }));
    setAdditionalParams(extraParams);

    const institutionId = searchParams.get("institution_id");
    if (referralCode) {
      fetch(`${API_BASE_URL}/api/v1/donation-referral/links/${encodeURIComponent(referralCode)}`)
        .then(async (r) => {
          const res = await r.json();
          if (!r.ok) throw new Error(res?.detail || "This donation link is not valid.");
          setReferral(res.data);
          setInstitutionName(res.data?.institution?.name ?? null);
        })
        .catch((err) => setReferralError(err.message));
    } else if (institutionId) {
      fetch(`${API_BASE_URL}/api/v1/common/institution/by_id/${institutionId}`)
        .then((r) => r.json())
        .then((res) => {
          console.log("Institution API response:", res);
          setInstitutionName(res?.name ?? null);
        })
        .catch((err) => console.error("Institution fetch error:", err));
    }

    const hasAllFields =
      amountFromUrl &&
      updatedFields.name &&
      updatedFields.mobile &&
      updatedFields.house &&
      updatedFields.place &&
      updatedFields.post &&
      updatedFields.pincode &&
      updatedFields.district &&
      updatedFields.state;

    if (hasAllFields) setActiveStep(1);
  }, [searchParams, referralCode]);

  const handleNext = () => {
    if (activeStep === 0 && !stepOneInput) {
      setErrorStepOne(true);
      return;
    }
    const mobileValid = formData.mobile.length > mobileDialCode.length;
    const otherFieldsValid = Object.entries(formData)
      .filter(([k]) => k !== "mobile")
      .every(([, v]) => Boolean(v));
    if (activeStep === 1 && (!mobileValid || !otherFieldsValid)) {
      setErrorStepTwo(true);
      return;
    }
    setErrorStepOne(false);
    setErrorStepTwo(false);
    setActiveStep((prev) => prev + 1);
  };

  const handleBack = () => setActiveStep((prev) => prev - 1);

  const handleReset = () => {
    setActiveStep(0);
    setStepOneInput("");
    setFormData(EMPTY_FORM);
    setAdditionalParams({});
    setErrorStepOne(false);
    setErrorStepTwo(false);
  };

  const handleSendOtp = async () => {
    const phone = encodeURIComponent(`+${formData.mobile}`);
    setOtpSending(true);
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/v1/dms/donor/send-otp?phone=${phone}`,
        { method: "POST" }
      );
      const res = await response.json();
      if (!response.ok) {
        alert(res.detail || "Failed to send OTP.");
        return;
      }
      setOtpValue(res?.otp ? String(res.otp) : "");
      setOtpError("");
      setOtpDialogOpen(true);
    } catch {
      alert("Failed to send OTP. Please try again.");
    } finally {
      setOtpSending(false);
    }
  };

  const handleVerifyOtp = async () => {
    const phone = encodeURIComponent(`+${formData.mobile}`);
    setOtpVerifying(true);
    setOtpError("");
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/v1/dms/donor/verify-otp?phone=${phone}&otp=${otpValue}`,
        { method: "POST" }
      );
      const res = await response.json();
      if (!response.ok) {
        setOtpError(res.detail || "Invalid or expired OTP.");
        return;
      }
      const { donor } = res;
      setFormData((prev) => ({
        ...prev,
        name: donor.name || prev.name,
        ...(donor.address && {
          house: donor.address.house || prev.house,
          place: donor.address.place || prev.place,
          post: donor.address.post || prev.post,
          pincode: donor.address.pin_code || prev.pincode,
          district: donor.address.district || prev.district,
          state: donor.address.state || prev.state,
        }),
      }));
      setOtpDialogOpen(false);
    } catch {
      setOtpError("Failed to verify OTP. Please try again.");
    } finally {
      setOtpVerifying(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stepOneInput) {
      setErrorStepOne(true);
      return;
    }
    const mobileValid = formData.mobile.length > mobileDialCode.length;
    const otherFieldsValid = Object.entries(formData)
      .filter(([k]) => k !== "mobile")
      .every(([, v]) => Boolean(v));
    if (!mobileValid || !otherFieldsValid) {
      setErrorStepTwo(true);
      return;
    }
    setLoading(true);

    const v = (val) => val?.trim() || null;
    const requestBody = {
      phone: formData.mobile.length > mobileDialCode.length ? `+${formData.mobile}` : null,
      amount: stepOneInput,
      name: v(formData.name),
      house: v(formData.house),
      place: v(formData.place),
      post: v(formData.post),
      pincode: v(formData.pincode),
      district: v(formData.district),
      state: v(formData.state),
      ...additionalParams,
    };

    try {
      const institutionId = searchParams.get("institution_id");
      const bankId = searchParams.get("bank_id") || "5";
      const apiUrl = referralCode
        ? `${API_BASE_URL}/api/v1/donation-referral/links/${encodeURIComponent(referralCode)}/pay`
        : `${API_BASE_URL}/api/v1/gateway-integration/public-donation/rzr-payment?bank_id=${bankId}${
            institutionId ? `&institution_id=${institutionId}` : ""
          }`;
      // Where Success / Fail send the donor back to.
      const backTo = referralCode
        ? `/r/${referralCode}${window.location.search}`
        : `/${window.location.search}`;

      const response = await fetch(apiUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer test",
        },
        body: JSON.stringify(requestBody),
      });

      const res = await response.json();
      if (!response.ok) {
        alert(res?.detail || "Something went wrong. Please try again.");
        return;
      }
      const orderData = res["data"];

      const options = {
        key: orderData["razorpay_key"],
        amount: orderData["fee"],
        currency: "INR",
        name: orderData["name"],
        description: "Donation",
        order_id: orderData["id"],
        handler: (response) => {
          navigate("/success", {
            state: {
              amount: stepOneInput,
              name: formData.name,
              orderId: response.razorpay_order_id,
              search: window.location.search,
              backTo,
            },
          });
        },
        prefill: {
          name: orderData["name"],
          contact: orderData["phone"],
          email: "sirajulhudalabs@gmail.com",
        },
        theme: { color: "#1a73e8" },
        modal: {
          ondismiss: () => navigate("/failed", { state: { search: window.location.search, backTo } }),
        },
      };

      new window.Razorpay(options).open();
    } catch (error) {
      console.error("Payment error:", error);
      alert("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-home">
      <div className="container-donation">
        <div className="donation-header">
          <span className="header-icon">🤲</span>
          {referral?.referrer_name && (
            <div className="referral-badge">Shared by {referral.referrer_name}</div>
          )}
          {institutionName ? (
            <>
              <h2>{institutionName}</h2>
              <p>Make a donation</p>
            </>
          ) : (
            <>
              <h2>Make a Donation</h2>
              <p>Your generosity makes a difference</p>
            </>
          )}
        </div>

        {referralError ? (
          <div className="referral-error">
            <p>{referralError}</p>
            <Button variant="outlined" onClick={() => navigate("/")}>
              Donate without the link
            </Button>
          </div>
        ) : (
        <Stepper activeStep={activeStep} orientation="vertical">
          {steps.map((label, index) => (
            <Step key={index}>
              <StepLabel>{label}</StepLabel>
              <StepContent>
                {index === 0 && (
                  <div>
                    <div className="preset-amounts">
                      {PRESET_AMOUNTS.map((amt) => (
                        <Chip
                          key={amt}
                          label={`₹${amt}`}
                          onClick={() => setStepOneInput(String(amt))}
                          variant={stepOneInput === String(amt) ? "filled" : "outlined"}
                          color="primary"
                        />
                      ))}
                    </div>
                    <TextField
                      fullWidth
                      variant="outlined"
                      type="number"
                      label="Or enter custom amount"
                      value={stepOneInput}
                      onChange={(e) => setStepOneInput(e.target.value)}
                      error={errorStepOne}
                      helperText={errorStepOne ? "Amount is required" : ""}
                      style={{ marginTop: "12px" }}
                      InputProps={{
                        startAdornment: (
                          <span style={{ marginRight: 6, color: "#666" }}>₹</span>
                        ),
                      }}
                    />
                  </div>
                )}

                {index === 1 && (
                  <div>
                    {FIELD_LABELS.map((fieldLabel, i) => {
                      if (fieldLabel === "Mobile Number") {
                        const mobileEmpty = formData.mobile.length <= mobileDialCode.length;
                        return (
                          <div key={i} style={{ marginTop: "12px" }}>
                            <PhoneInput
                              country="in"
                              value={formData.mobile}
                              onChange={(value, countryData) => {
                                setMobileDialCode(countryData.dialCode);
                                setFormData((prev) => ({ ...prev, mobile: value }));
                              }}
                              specialLabel="Mobile Number *"
                              inputStyle={{
                                width: "100%",
                                borderColor: errorStepTwo && mobileEmpty ? "#d32f2f" : undefined,
                              }}
                              containerStyle={{ width: "100%" }}
                            />
                            {errorStepTwo && mobileEmpty && (
                              <p style={{ color: "#d32f2f", fontSize: "0.75rem", margin: "4px 14px 0" }}>
                                Mobile Number is required
                              </p>
                            )}
                            {!mobileEmpty && (
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={handleSendOtp}
                                disabled={otpSending}
                                style={{ marginTop: 8 }}
                              >
                                {otpSending ? <CircularProgress size={16} /> : "Send OTP"}
                              </Button>
                            )}
                          </div>
                        );
                      }
                      return (
                        <TextField
                          fullWidth
                          key={i}
                          variant="outlined"
                          label={fieldLabel}
                          required
                          value={formData[FIELD_MAP[fieldLabel]]}
                          onChange={(e) =>
                            setFormData((prev) => ({
                              ...prev,
                              [FIELD_MAP[fieldLabel]]: e.target.value,
                            }))
                          }
                          error={errorStepTwo && !formData[FIELD_MAP[fieldLabel]]}
                          helperText={
                            errorStepTwo && !formData[FIELD_MAP[fieldLabel]]
                              ? `${fieldLabel} is required`
                              : ""
                          }
                          style={{ marginTop: "12px" }}
                          type={fieldLabel === "Pincode" ? "number" : "text"}
                          inputProps={
                            fieldLabel === "Pincode"
                              ? { pattern: "[0-9]{6}", maxLength: 6 }
                              : undefined
                          }
                        />
                      );
                    })}
                  </div>
                )}

                <div style={{ marginTop: "16px", display: "flex", gap: "8px" }}>
                  <Button
                    variant="contained"
                    onClick={activeStep === steps.length - 1 ? handleSubmit : handleNext}
                    disabled={loading}
                  >
                    {loading ? (
                      <CircularProgress size={20} style={{ color: "white" }} />
                    ) : activeStep === steps.length - 1 ? (
                      "Donate Now"
                    ) : (
                      "Next"
                    )}
                  </Button>
                  {activeStep > 0 && (
                    <Button variant="outlined" onClick={handleBack}>
                      Back
                    </Button>
                  )}
                </div>
              </StepContent>
            </Step>
          ))}
        </Stepper>
        )}

        {activeStep === steps.length && (
          <div style={{ marginTop: "20px", textAlign: "center" }}>
            <Typography>All steps completed</Typography>
            <Button
              variant="outlined"
              onClick={handleReset}
              style={{ marginTop: "10px" }}
            >
              Reset
            </Button>
          </div>
        )}
      </div>

      <Dialog open={otpDialogOpen} onClose={() => setOtpDialogOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Enter OTP</DialogTitle>
        <DialogContent>
          <Typography variant="body2" style={{ marginBottom: 16 }}>
            An OTP has been sent to +{formData.mobile}
          </Typography>
          <TextField
            autoFocus
            fullWidth
            label="OTP"
            value={otpValue}
            onChange={(e) => { setOtpValue(e.target.value); setOtpError(""); }}
            error={Boolean(otpError)}
            helperText={otpError}
            inputProps={{ maxLength: 6 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOtpDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleVerifyOtp}
            disabled={otpVerifying || !otpValue}
          >
            {otpVerifying ? <CircularProgress size={20} style={{ color: "white" }} /> : "Verify"}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default Home;
